import { createApi } from '@reduxjs/toolkit/dist/query/react';

import serverRoutes from '@app/utils/server-routes';

import {
  UsersListType,
  UserRoleList,
  UserObjectType,
  UserGroupBulkType,
  UserGroupType,
  UserGroupCreateType,
  UsersListRequestType,
  UsersType,
} from '@app/interfaces/users';

import { baseQuery } from '../baseQuery';

export const usersApi = createApi({
  reducerPath: 'usersApi',
  tagTypes: ['users', 'singleUser', 'userGroups', 'userGroupSingle'],
  baseQuery: baseQuery,
  endpoints: (build) => ({
    getUsers: build.query<UsersListType, UsersListRequestType>({
      query: (params) => ({
        url: serverRoutes.usersBulk,
        params,
      }),
      providesTags: ['users'],
    }),
    getUsersShort: build.query<{ users: Pick<UsersType, 'id' | 'username'>[] }, void>({
      query: () => ({
        url: serverRoutes.usersShortBulk,
      }),
    }),
    getUserRole: build.query<UserRoleList, void>({
      query: () => ({
        url: serverRoutes.usersRole,
      }),
      providesTags: ['singleUser'],
    }),
    addUser: build.mutation<
      UserObjectType,
      { username: string; role_id: string; login: string; password?: string }
    >({
      query: (body) => ({
        url: serverRoutes.users,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['users'],
    }),
    deleteUser: build.mutation({
      query: (body: string) => ({
        url: serverRoutes.users,
        method: 'DELETE',
        params: { id: body },
      }),
      invalidatesTags: ['users', 'singleUser'],
    }),
    editUser: build.mutation<UserObjectType, UserObjectType>({
      query: (body) => ({
        url: serverRoutes.users,
        method: 'PUT',
        params: { id: body.id },
        body,
      }),
      invalidatesTags: ['users', 'singleUser'],
    }),

    // userGroups
    getUserGroupsBulk: build.query<{ groups: UserGroupBulkType[] }, void>({
      query: () => ({
        url: serverRoutes.userGroupsBulk,
      }),
      providesTags: ['userGroups'],
    }),
    getUserGroupInfo: build.query<UserGroupType, { id: string }>({
      query: (params) => ({
        url: serverRoutes.userGroups,
        params,
      }),
      providesTags: ['userGroupSingle'],
    }),
    createUserGroup: build.mutation<UserGroupType, Pick<UserGroupCreateType, 'name' | 'users'>>({
      query: (body) => ({
        url: serverRoutes.userGroups,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['userGroups'],
    }),
    editUserGroup: build.mutation<
      UserGroupType,
      { body: Pick<UserGroupCreateType, 'name' | 'users'>; params: { id: string } }
    >({
      query: ({ body, params }) => ({
        url: serverRoutes.userGroups,
        method: 'PUT',
        body,
        params,
      }),
      invalidatesTags: ['userGroups', 'userGroupSingle'],
    }),
    deleteUserGroup: build.mutation({
      query: (id: string) => ({
        url: serverRoutes.userGroups,
        method: 'DELETE',
        params: { id },
      }),
      invalidatesTags: ['userGroups', 'userGroupSingle'],
    }),
    // mutate single user to group
    deleteUserFromGroup: build.mutation({
      query: (params: { id: string; user_id: string }) => ({
        url: serverRoutes.removeUserFromGroup,
        method: 'PUT',
        params: { id: params.id },
        body: { user_id: params.user_id },
      }),
      invalidatesTags: ['users'],
    }),
    addUserToGroup: build.mutation({
      query: (params: { id: string; user_id: string }) => ({
        url: serverRoutes.addUserToGroup,
        method: 'PUT',
        params: { id: params.id },
        body: { user_id: params.user_id },
      }),
      invalidatesTags: ['users'],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetUserRoleQuery,
  useAddUserMutation,
  useEditUserMutation,
  useDeleteUserMutation,
  useGetUsersShortQuery,

  useGetUserGroupsBulkQuery,
  useCreateUserGroupMutation,
  useDeleteUserGroupMutation,
  useLazyGetUserGroupInfoQuery,
  useEditUserGroupMutation,

  useAddUserToGroupMutation,
  useDeleteUserFromGroupMutation,
} = usersApi;
